import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import TrustBudDashboardPage from "./pages/TrustBudDashboardPage";
import TwilioVerifyPage from "./pages/TwilioVerifyPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import WeeklySummaryPage from "./pages/WeeklySummaryPage";
import TrustBudConnectionDataPage from "./pages/TrustBudConnectionDataPage";
import NetworkPage from "./pages/NetworkPage";
import MomentDetailPage from "./pages/MomentDetailPage";
import ConnectionsPage from "./pages/ConnectionsPage";
import PersonContextPage from "./pages/PersonContextPage";
import PlanPage from "./pages/PlanPage";
import IntelligencePage from "./pages/IntelligencePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import AccountMergeConfirmPage from "./pages/AccountMergeConfirmPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import AdminAnalyticsPage from "./pages/AdminAnalyticsPage";
import AdminNudgeAuditPage from "./pages/AdminNudgeAuditPage";
import AdminAiDebugPage from "./pages/AdminAiDebugPage";
import HowTrustBudWorksPage from "./pages/HowTrustBudWorksPage";
import TrustBudPlusPage from "./pages/TrustBudPlusPage";
import MarketingHomePage from "./pages/MarketingHomePage";
import SeoLandingPage, { SEO_LANDING_PAGES } from "./pages/SeoLandingPage";

import AppLayout from "./layouts/AppLayout";
import { hasAuthSession, logout } from "./api/auth";
import { apiFetch } from "./api/apiFetch";
import { updateUserTimezone } from "./api/user";
import { createPresenceCheckin } from "./api/checkins";

const APP_STORE_URL = "https://apps.apple.com/us/app/trustbud/id6757308952";

function PreserveSearchRedirect({ to }) {
  const location = useLocation();
  const nextPath = `${to}${location.search || ""}`;
  return <Navigate to={nextPath} replace />;
}

function AppStoreRedirect() {
  useEffect(() => {
    window.location.replace(APP_STORE_URL);
  }, []);

  return (
    <div style={{ padding: 24, fontFamily: "Inter, system-ui, sans-serif" }}>
      <h1 style={{ margin: "0 0 8px", fontSize: 24 }}>Opening TrustBud</h1>
      <p style={{ margin: 0 }}>
        If nothing happens, <a href={APP_STORE_URL}>open TrustBud in the App Store</a>.
      </p>
    </div>
  );
}

function MessagesRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search || "");
  params.set("tab", "messages");
  return <Navigate to={`/activity?${params.toString()}`} replace />;
}

function safeNextPath(search) {
  const next = new URLSearchParams(search || "").get("next");
  if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  return "/";
}

function LoginRedirect() {
  const location = useLocation();
  const next = `${location.pathname}${location.search || ""}`;
  return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
}

function LoginGate({
  isLoggedIn,
  showRegister,
  setShowRegister,
  setIsLoggedIn,
  inviteContext,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  if (isLoggedIn) {
    return <Navigate to={safeNextPath(location.search)} replace />;
  }

  const openLogin = () => {
    setShowRegister(false);
    navigate(`/login${location.search || ""}`, { replace: true });
  };
  const openRegister = () => {
    setShowRegister(true);
    navigate(`/register${location.search || ""}`);
  };

  return showRegister ? (
    <RegisterPage
      goToLogin={openLogin}
      onRegistered={() => {
        setIsLoggedIn(true);
        setShowRegister(false);
      }}
    />
  ) : (
    <LoginPage
      onLogin={() => setIsLoggedIn(true)}
      goToRegister={openRegister}
      inviteContext={inviteContext}
    />
  );
}

function RegisterRoute({ isLoggedIn, setShowRegister, setIsLoggedIn }) {
  const location = useLocation();
  const navigate = useNavigate();

  if (isLoggedIn) {
    return <Navigate to={safeNextPath(location.search)} replace />;
  }

  return (
    <RegisterPage
      goToLogin={() => {
        setShowRegister(false);
        navigate(`/login${location.search || ""}`, { replace: true });
      }}
      onRegistered={() => {
        setIsLoggedIn(true);
        setShowRegister(false);
      }}
    />
  );
}

function AdminOnlyRoute({ children }) {
  const [state, setState] = useState({ loading: true, allowed: false });

  useEffect(() => {
    let mounted = true;
    apiFetch("/account-settings/")
      .then((account) => {
        if (mounted) setState({ loading: false, allowed: Boolean(account?.is_staff) });
      })
      .catch(() => {
        if (mounted) setState({ loading: false, allowed: false });
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (state.loading) return null;
  if (state.allowed) return children;
  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ margin: 0, fontSize: 24 }}>Admin access required</h1>
      <p style={{ maxWidth: 560 }}>
        This page is only available when the current TrustBud web session belongs to a staff account.
        Log out and sign back in with the staff account you want to use.
      </p>
    </div>
  );
}

function AdminAnalyticsRoute() {
  return (
    <AdminOnlyRoute>
      <AdminAnalyticsPage />
    </AdminOnlyRoute>
  );
}

function AdminNudgeAuditRoute() {
  return (
    <AdminOnlyRoute>
      <AdminNudgeAuditPage />
    </AdminOnlyRoute>
  );
}

function AdminAiDebugRoute() {
  return (
    <AdminOnlyRoute>
      <AdminAiDebugPage />
    </AdminOnlyRoute>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(hasAuthSession());
  const [showRegister, setShowRegister] = useState(false);
  const [inviteContext, setInviteContext] = useState({ inviter: "", valid: false });

  useEffect(() => {
    function handleLogout() {
      setIsLoggedIn(false);
    }

    window.addEventListener("auth-logout", handleLogout);
    return () => window.removeEventListener("auth-logout", handleLogout);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;

    const presenceKey = "presence_checkin_done";
    if (!sessionStorage.getItem(presenceKey)) {
      createPresenceCheckin().catch((error) => {
        console.error("Failed to record presence check-in", error);
      });
      sessionStorage.setItem(presenceKey, "true");
    }

    const timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

    updateUserTimezone(timezone).catch((error) => {
      console.error("Failed to update timezone", error);
    });
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) return;
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get("invite") || params.get("invite_token");
    if (!inviteToken) return;

    localStorage.setItem("invite_token", inviteToken);
    const apiUrl = process.env.REACT_APP_API_URL || "";

    fetch(`${apiUrl}/invites/${inviteToken}/`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data?.valid) {
          setInviteContext({ inviter: data.inviter || "", valid: true });
        } else {
          setInviteContext({ inviter: "", valid: false });
        }
      })
      .catch(() => {
        setInviteContext({ inviter: "", valid: false });
      });
  }, [isLoggedIn]);

  return (
    <Router>
      <Routes>
        {/* ───────────── PUBLIC ROUTES ───────────── */}
        <Route path="/twilio/verify" element={<TwilioVerifyPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/account-merge" element={<AccountMergeConfirmPage />} />
        <Route path="/app" element={<AppStoreRedirect />} />
        <Route path="/download" element={<AppStoreRedirect />} />
        <Route path="/invite" element={<PreserveSearchRedirect to="/register" />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        {!isLoggedIn && <Route path="/" element={<MarketingHomePage />} />}
        {!isLoggedIn &&
          SEO_LANDING_PAGES.map((page) => (
            <Route
              key={page.path}
              path={page.path}
              element={<SeoLandingPage pageKey={page.key} />}
            />
          ))}
        {!isLoggedIn && <Route path="/today" element={<LoginRedirect />} />}
        {!isLoggedIn && <Route path="/sprint" element={<LoginRedirect />} />}
        {!isLoggedIn && <Route path="/challenge" element={<LoginRedirect />} />}
        {!isLoggedIn && <Route path="/updates" element={<LoginRedirect />} />}
        {!isLoggedIn && <Route path="/activity" element={<LoginRedirect />} />}
        {!isLoggedIn && <Route path="/moments/:momentId" element={<LoginRedirect />} />}
        {!isLoggedIn && <Route path="/messages" element={<LoginRedirect />} />}
        {!isLoggedIn && <Route path="/network" element={<LoginRedirect />} />}
        {!isLoggedIn && <Route path="/connections" element={<LoginRedirect />} />}
        {!isLoggedIn && <Route path="/plan" element={<LoginRedirect />} />}
        {!isLoggedIn && (
          <Route
            path="/login"
            element={
              <LoginGate
                isLoggedIn={isLoggedIn}
                showRegister={showRegister}
                setShowRegister={setShowRegister}
                setIsLoggedIn={setIsLoggedIn}
                inviteContext={inviteContext}
              />
            }
          />
        )}
        {!isLoggedIn && (
          <Route
            path="/register"
            element={
              <RegisterRoute
                isLoggedIn={isLoggedIn}
                setShowRegister={setShowRegister}
                setIsLoggedIn={setIsLoggedIn}
              />
            }
          />
        )}
        {!isLoggedIn && (
          <Route path="/trustbud-plus" element={<TrustBudPlusPage />} />
        )}

        {/* ───────────── AUTHENTICATED APP ───────────── */}
        <Route
          path="/*"
          element={
            isLoggedIn ? (
              <AppLayout
                username={localStorage.getItem("username")}
                onLogout={() => {
                  logout();
                  setIsLoggedIn(false);
                }}
              >
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/today" element={<DashboardPage />} />
                  <Route path="/weekly-summary" element={<WeeklySummaryPage />} />
                  <Route
                    path="/companions"
                    element={<Navigate to="/connections?tab=find" replace />}
                  />
                  <Route path="/connections" element={<ConnectionsPage />} />
                  <Route
                    path="/accountability"
                    element={<TrustBudConnectionDataPage />}
                  />
                  <Route
                    path="/sprint"
                    element={<PreserveSearchRedirect to="/accountability" />}
                  />
                  <Route
                    path="/challenge"
                    element={<PreserveSearchRedirect to="/accountability" />}
                  />
                  <Route
                    path="/trustbud-connection-data"
                    element={<PreserveSearchRedirect to="/sprint" />}
                  />
                  <Route
                    path="/trustbud-timeline"
                    element={<PreserveSearchRedirect to="/activity" />}
                  />
                  {/* Legacy route: Network now maps to the Activity hub; People lives at /connections. */}
                  <Route path="/network" element={<PreserveSearchRedirect to="/activity" />} />
                  <Route
                    path="/activity"
                    element={<NetworkPage />}
                  />
                  <Route
                    path="/moments/:momentId"
                    element={<MomentDetailPage />}
                  />
                  <Route path="/updates" element={<PreserveSearchRedirect to="/activity" />} />
                  <Route path="/messages" element={<MessagesRedirect />} />
                  <Route
                    path="/network/person/:connectionId"
                    element={<PersonContextPage />}
                  />
                  <Route
                    path="/updates/person/:connectionId"
                    element={<PersonContextPage />}
                  />
                  <Route
                    path="/manage-vision"
                    element={<Navigate to="/plan" replace />}
                  />
                  <Route
                    path="/plan"
                    element={<PlanPage />}
                  />
                  <Route
                    path="/intelligence"
                    element={<IntelligencePage />}
                  />
                  <Route
                    path="/account-settings"
                    element={<AccountSettingsPage />}
                  />
                  <Route
                    path="/admin"
                    element={<AdminNudgeAuditRoute />}
                  />
                  <Route
                    path="/admin/*"
                    element={<AdminNudgeAuditRoute />}
                  />
                  <Route
                    path="/admin/analytics"
                    element={<AdminAnalyticsRoute />}
                  />
                  <Route
                    path="/admin/nudges"
                    element={<AdminNudgeAuditRoute />}
                  />
                  <Route
                    path="/admin/ai"
                    element={<AdminAiDebugRoute />}
                  />
                  <Route
                    path="/how-trustbud-works"
                    element={<HowTrustBudWorksPage />}
                  />
                  <Route
                    path="/trustbud-plus"
                    element={<TrustBudPlusPage />}
                  />
                  <Route
                    path="/trustbuds/:userId"
                    element={<TrustBudDashboardPage />}
                  />
                  <Route
                    path="/sprints/:sprintId"
                    element={<TrustBudDashboardPage />}
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AppLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
